import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { isString } from 'lodash';

const keysToRemove = ['tax_amount', 'charge_amount', 'discount_amount'];

export const mutateCustomer = async ({ payload, isEdit }) => {
  if (isEdit) {
    const { data } = await apiClient.put('/customers/' + payload.profile_id, payload);
    return data;
  } else {
    const { data } = await apiClient.post('/customers', payload);
    return data;
  }
};

export const useMutateCustomerHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { snackbarMessages } = useTranslatedMessages();
  const customerMessages = snackbarMessages.customers ?? {};
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: mutateCustomer,
    onSuccess: (data, variables) => {
      dispatch(showSnackbar({ message: customerMessages.customer_saved_success, severity: 'success' }));
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      onSuccess && onSuccess(variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          setError(error.response.data.errors);
          return;
        }
        const apiMessage = error.response?.data?.error || error.response?.data?.message || error.message;
        dispatch(showSnackbar({ message: apiMessage, severity: 'error' }));
      } else {
        dispatch(
          showSnackbar({
            message: isString(error.message) ? error.message : JSON.stringify(error.message),
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = async (data, isEdit) => {
    const cleanedData = Object.fromEntries(Object.entries(data).filter(([key]) => !keysToRemove.includes(key)));
    mutation.mutate({ payload: cleanedData, isEdit });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
